import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { GOOGLE_CLIENT_ID } from '../config/env.js';
import { MOBILE_OAUTH_REDIRECT, MOBILE_APP_CALLBACK } from './platform.js';

const OAUTH_SCOPES = ['openid', 'email', 'profile'].join(' ');

function parseAccessTokenFromUrl(url) {
  if (!url) return null;
  const hash = url.includes('#') ? url.split('#')[1] : '';
  const query = url.includes('?') ? url.split('?')[1]?.split('#')[0] : '';
  const params = new URLSearchParams(hash || query);
  return params.get('access_token');
}

function buildGoogleAuthUrl(clientId) {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: MOBILE_OAUTH_REDIRECT,
    response_type: 'token',
    scope: OAUTH_SCOPES,
    prompt: 'select_account',
    include_granted_scopes: 'true',
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/**
 * Google Sign-In for Capacitor — opens system browser, returns access token for /api/auth/google.
 * Register redirect URI on the Web OAuth client:
 *   https://repatikosam.com/app/oauth2redirect.html
 */
export function signInWithGoogleNative() {
  if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === 'DEMO') {
    return Promise.reject(new Error('Google Client ID is not configured for mobile.'));
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    };

    const urlListener = App.addListener('appUrlOpen', (event) => {
      const token = parseAccessTokenFromUrl(event.url);
      if (token) {
        Browser.close().catch(() => {});
        finish(resolve, { access_token: token });
      } else if (event.url?.startsWith(MOBILE_APP_CALLBACK)) {
        finish(reject, new Error('Google sign-in did not return a token.'));
      }
    });

    const stateListener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        Browser.close().catch(() => {});
      }
    });

    const cleanup = () => {
      urlListener.then((h) => h.remove()).catch(() => {});
      stateListener.then((h) => h.remove()).catch(() => {});
    };

    const authUrl = buildGoogleAuthUrl(GOOGLE_CLIENT_ID);
    Browser.open({ url: authUrl, presentationStyle: 'popover' }).catch((err) => {
      finish(reject, err instanceof Error ? err : new Error('Could not open Google sign-in.'));
    });

    window.setTimeout(() => {
      finish(reject, new Error('Google sign-in timed out. Please try again.'));
    }, 120000);
  });
}
